import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { errMsg, formatDate, useApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import AppShell from "@/components/layout/AppShell";
import { ActivationApproveForm } from "@/components/ops/ActivationApproveForm";
import { ActivationTestSend } from "@/components/ops/ActivationTestSend";

const TONE = { PASS: "text-emerald-700", WARN: "text-amber-700", BLOCKED: "text-red-700" };

function Checks({ pf, testid }) {
  return (
    <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white" data-testid={testid}>
      {pf.checks.map((c) => (
        <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-sm" data-testid={`${testid}-${c.id}`}>
          <span className="text-slate-800">{c.label}</span>
          <span className="text-xs"><span className={`mr-2 font-semibold ${TONE[c.status]}`}>{c.status}</span><span className="text-slate-500">{c.detail}</span></span>
        </li>
      ))}
    </ul>
  );
}

// MUSORA internal admins only: activation gate for real tenant emails. This page never sends anything.
export default function EmailActivation() {
  const api = useApi();
  const [d, setD] = useState(null);
  const [dry, setDry] = useState(null);
  const load = useCallback(() => api.get("/admin/email-activation").then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e))), [api]);
  useEffect(() => { load(); }, [load]);
  const revoke = async () => { try { await api.post("/admin/email-activation/revoke"); toast.success("Invio bloccato"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const st = d?.state;
  const bl = d?.preflight.backlog;
  return (
    <AppShell>
      <div className="mx-auto max-w-5xl space-y-5 p-4 sm:p-6" data-testid="activation-page">
        <header><h1 className="text-2xl font-semibold text-slate-900">Attivazione email reali</h1>
          <p className="text-sm text-slate-600">Solo staff MUSORA. Servono interruttore di ambiente, approvazione esplicita qui, integrazione attiva del cliente; il pregresso resta sempre bloccato.</p>
          <p className="mt-1 text-xs text-slate-600" data-testid="activation-scope">{d?.scope === "general" ? "Modalità generale: tutti i clienti approvati (anche «*»), con link di disiscrizione, limiti anti-spam per cliente e per destinatario. Il cliente pilota resta con le regole della sandbox." : "Modalità pilota: solo il cliente pilota e i destinatari autorizzati."}</p></header>
        {!d ? <p className="text-sm text-slate-500">Caricamento…</p> : (<>
          <section className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border p-4 ${st.live ? "border-red-300 bg-red-50" : "border-emerald-200 bg-emerald-50"}`} data-testid="activation-state">
            <div className="text-sm">
              <p className="font-semibold" data-testid="activation-state-label">{st.live ? `APPROVATO${st.dev_rehearsal ? " (prova DEV)" : ""} per ${st.tenants.length} clienti fino al ${formatDate(st.expires_at, true)}` : "Invio reale BLOCCATO"}</p>
              <p className="text-xs text-slate-600">{st.approved_at ? `Ultima approvazione ${formatDate(st.approved_at, true)} da ${st.approved_by}` : "Mai approvato"}{st.revoked_at ? ` · revocato ${formatDate(st.revoked_at, true)}` : ""}</p>
            </div>
            {st.live && <Button variant="destructive" onClick={revoke} data-testid="activation-revoke">Blocca subito</Button>}
          </section>
          <section className="space-y-2"><h2 className="text-base font-semibold text-slate-900">Controlli preliminari {d.preflight.ready ? "· pronti" : "· NON pronti"}</h2><Checks pf={d.preflight} testid="activation-checks" /></section>
          <p className="text-sm text-slate-700" data-testid="activation-backlog">Pregresso: {bl.total} email già create, <b>{bl.will_be_sent} verranno inviate</b> · {Object.entries(bl.by_status).map(([k, v]) => `${k} ${v}`).join(" · ")}</p>
          {d.pilot && <p className="text-sm text-slate-700" data-testid="activation-pilot">Cliente pilota di questo ambiente (dal database): <b>{d.pilot.name}</b> · <code>{d.pilot.id}</code></p>}
          <ActivationApproveForm phrase={d.phrase} pilot={d.pilot} onDone={(data) => { if (data.simulated) setDry(data); load(); }} />
          {st.live && <ActivationTestSend tenants={st.tenants} />}
          {dry && <section className="space-y-2" data-testid="activation-dry-result"><h2 className="text-base font-semibold text-slate-900">Esito della prova · {dry.ready ? "pronto" : "non pronto"}</h2><Checks pf={dry} testid="activation-dry-checks" /></section>}
        </>)}
      </div>
    </AppShell>
  );
}
